'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/core/presentation/components/ui/Button';
import AppIcon from '@/core/presentation/components/ui/AppIcon';
import { Spinner } from '@/core/presentation/components/ui/Spinner';
import { SectionScreenLayout } from '@/features/app/presentation/components/SectionScreenLayout';
import { useServices } from '../context/ServiceContext';
import { useToast } from '@/core/presentation/components/Toast';
import { ConfirmModal } from '@/core/presentation/components/ui/ConfirmModal';
import { LISTA_SERVICOS_DELETE_MODAL } from '@/features/app/services/presentation/constants/lista-servicos-delete-modal-copy';
import { LISTA_SERVICOS_REGISTER_SERVICE_BUTTON } from '@/features/app/services/presentation/constants/lista-servicos-section-copy';
import type { Client, Service } from '@/core/domain';
import { SERVICE_TYPE_LABELS } from '@/core/domain';
import { useClients } from '@/features/app/clients/presentation/context/ClientContext';
import { useAwaitInitialFetch } from '@/features/app/presentation/hooks/use-await-initial-fetch';

function getServiceTypeDisplayName(service: Service): string {
  if (service.type === 'outro' && service.type_other_description?.trim()) {
    return service.type_other_description.trim();
  }
  return SERVICE_TYPE_LABELS[service.type];
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  return new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export interface ListaServicosSectionProps {
  /** Navegar para a seção de registrar serviço */
  onRegisterClick?: () => void;
  /** Navegar para editar o serviço (ex.: abrir formulário de registro com dados do serviço) */
  onEditService?: (service: Service) => void;
}

export const ListaServicosSection: React.FC<ListaServicosSectionProps> = ({
  onRegisterClick,
  onEditService,
}) => {
  const { state, events } = useServices();
  const { state: clientsState, events: clientsEvents } = useClients();
  const toast = useToast();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [servicePendingDelete, setServicePendingDelete] = useState<Service | null>(null);
  const [showFloatingButton, setShowFloatingButton] = useState(true);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  const loadLista = useCallback(async () => {
    await Promise.all([events.getByUserId(), clientsEvents.getByUserId()]);
  }, [events.getByUserId, clientsEvents.getByUserId]);
  const listaDataReady = useAwaitInitialFetch(loadLista, [loadLista]);

  useEffect(() => {
    if (!onRegisterClick) return;
    const handleScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      requestAnimationFrame(() => {
        const scrollY = window.scrollY ?? document.documentElement.scrollTop;
        const delta = scrollY - lastScrollY.current;
        if (Math.abs(delta) > 30) {
          if (delta > 0) {
            setShowFloatingButton(false);
          } else {
            setShowFloatingButton(true);
          }
        }
        lastScrollY.current = scrollY;
        ticking.current = false;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [onRegisterClick]);

  const handleConfirmDelete = async (): Promise<boolean> => {
    if (!servicePendingDelete) return false;
    setDeletingId(servicePendingDelete.id);
    try {
      const result = await events.delete(servicePendingDelete.id);
      if (result.success) {
        toast.success(LISTA_SERVICOS_DELETE_MODAL.toastSuccess);
        return true;
      }
      toast.error(result.error ?? LISTA_SERVICOS_DELETE_MODAL.toastError);
      return false;
    } finally {
      setDeletingId(null);
    }
  };

  const services = state.services;
  const clientsById = new Map<string, Client>(
    clientsState.clients.map((client) => [client.id, client])
  );

  const getServiceAddress = (client?: Client): string | null => {
    if (!client) return null;
    if (client.address?.trim()) return client.address.trim();
    if (client.reference?.trim()) return client.reference.trim();
    return null;
  };

  return (
    <SectionScreenLayout
      title="Serviços"
      subtitle="Lista dos serviços registrados. Você pode editar ou excluir um serviço."
    >
      <div className="space-y-4">
        {(state.error || clientsState.error) && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {state.error ?? clientsState.error}
          </div>
        )}

        {!listaDataReady ? (
          <div className="flex items-center gap-2 text-text-muted text-sm py-2">
            <Spinner size="sm" className="text-cyan" aria-hidden />
            <span>Carregando</span>
          </div>
        ) : services.length === 0 ? (
          <div className="rounded-xl border border-border-light bg-[#fafbfc] p-8 text-center">
            <p className="text-text-muted text-sm">
              Nenhum serviço registrado. Registre um serviço para começar.
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {services.map((s: Service) => {
              const client = clientsById.get(s.client_id);
              const clientName = client?.name?.trim() || 'Cliente não encontrado';
              const serviceAddress = getServiceAddress(client);
              return (
              <li
                key={s.id}
                className="rounded-xl border border-border-light bg-white p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-navy-dark">
                    {getServiceTypeDisplayName(s)} — {formatDate(s.service_date)}
                  </p>
                  <p className="text-sm text-text-muted mt-0.5">
                    Próxima manutenção: {formatDate(s.next_maintenance_date)}
                  </p>
                  <p className="text-sm text-text-muted mt-0.5 truncate">
                    {serviceAddress ? `${clientName} - ${serviceAddress}` : clientName}
                  </p>
                  {s.equipment_reference && (
                    <p className="text-xs text-text-muted mt-1 truncate">
                      {s.equipment_reference}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {onEditService && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="gap-1.5 !text-cyan"
                      onClick={() => onEditService(s)}
                      aria-label="Editar serviço"
                    >
                      <AppIcon name="PencilSquareIcon" size={18} />
                      Editar
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="gap-1.5 !text-red-600 hover:!bg-red-50"
                    onClick={() => setServicePendingDelete(s)}
                    disabled={deletingId === s.id || servicePendingDelete !== null}
                    aria-label="Excluir serviço"
                  >
                    <AppIcon name="TrashIcon" size={18} />
                    Excluir
                  </Button>
                </div>
              </li>
              );
            })}
          </ul>
        )}
      </div>

      {onRegisterClick ? <div className="h-16 sm:h-20" aria-hidden /> : null}

      {onRegisterClick ? (
        <div
          className={`fixed z-40 transition-all duration-300 ease-out bottom-[calc(1.5rem+env(safe-area-inset-bottom,0px))] right-[calc(1.5rem+env(safe-area-inset-right,0px))] ${
            showFloatingButton
              ? 'opacity-100 translate-y-0 pointer-events-auto'
              : 'opacity-0 translate-y-4 pointer-events-none'
          }`}
        >
          <Button type="button" size="lg" className="gap-2 shadow-lg" onClick={onRegisterClick}>
            <AppIcon name="PlusCircleIcon" size={22} />
            {LISTA_SERVICOS_REGISTER_SERVICE_BUTTON}
          </Button>
        </div>
      ) : null}

      <ConfirmModal
        isOpen={servicePendingDelete !== null}
        onClose={() => setServicePendingDelete(null)}
        title={LISTA_SERVICOS_DELETE_MODAL.title}
        cancelLabel={LISTA_SERVICOS_DELETE_MODAL.cancelLabel}
        confirmLabel={LISTA_SERVICOS_DELETE_MODAL.confirmLabel}
        confirmVariant="danger"
        onConfirm={handleConfirmDelete}
      >
        <p className="text-sm text-text-muted leading-relaxed">
          {LISTA_SERVICOS_DELETE_MODAL.bodyIntro}
        </p>
        {servicePendingDelete ?
          <div className="mt-4 rounded-lg border border-border-light bg-[#fafbfc] px-3 py-2.5 text-sm">
            <p className="font-medium text-navy-dark">
              {getServiceTypeDisplayName(servicePendingDelete)} —{' '}
              {formatDate(servicePendingDelete.service_date)}
            </p>
            <p className="text-text-muted text-xs mt-1 truncate">
              {clientsById.get(servicePendingDelete.client_id)?.name?.trim() || 'Cliente'}
            </p>
          </div>
        : null}
      </ConfirmModal>
    </SectionScreenLayout>
  );
};
